import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiPortalBackend } from '../../apis/ApiPortalBackend';
import { requestWalletRefresh } from '../../utils/walletRefresh';

// Return targets for the hosted checkout. PaymentService's SUCCESS_URL/CANCEL_URL (its server
// .env) must point here, e.g. https://stage.app.sengilabs.ai/payment/success. Query params on
// success: Stripe appends ?session_id=...; our PayPal return_url carries
// ?provider=paypal&payment_type=prepaid|subscription and PayPal itself appends token=<order_id>.

export const PaymentSuccessPage = () => {
    const { t } = useTranslation('platformPages');
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<'working' | 'done' | 'failed'>('working');
    // PayPal prepaid orders must be captured after approval - exactly once, even under React
    // StrictMode's double-invoked effects (the capture endpoint charges the buyer).
    const captureStartedRef = useRef(false);

    useEffect(() => {
        document.title = t('paymentReturn.processingTitle');
        const provider = searchParams.get('provider');
        const paymentType = searchParams.get('payment_type');
        const paypalOrderId = searchParams.get('token');

        if (provider === 'paypal' && paymentType === 'prepaid' && paypalOrderId) {
            if (captureStartedRef.current) return;
            captureStartedRef.current = true;
            ApiPortalBackend.CapturePayPalOrder(paypalOrderId).then(result => {
                setStatus(result?.success ? 'done' : 'failed');
                if (result?.success) requestWalletRefresh(2000);
            });
        } else {
            // Stripe (and PayPal subscriptions) are credited via server-side webhooks - nothing
            // to do client-side beyond refreshing the displayed balance.
            setStatus('done');
            requestWalletRefresh(2000);
        }
    }, [searchParams]);

    return (
        <div className="container py-5 text-center">
            {status === 'working' && (
                <>
                    <div className="spinner-border mb-3" role="status"><span className="visually-hidden">{t('paymentReturn.processing')}</span></div>
                    <h3>{t('paymentReturn.processingHeading')}</h3>
                </>
            )}
            {status === 'done' && (
                <>
                    <h3>✅ {t('paymentReturn.successHeading')}</h3>
                    <p className="text-secondary">
                        {t('paymentReturn.successBody')}
                    </p>
                </>
            )}
            {status === 'failed' && (
                <>
                    <h3>⚠️ {t('paymentReturn.failedHeading')}</h3>
                    <p className="text-secondary">
                        {t('paymentReturn.failedBody')}
                    </p>
                </>
            )}
            <Link to="/billing" className="btn btn-primary mt-3">{t('paymentReturn.toBilling')}</Link>
        </div>
    );
};

export const PaymentCancelPage = () => {
    const { t } = useTranslation('platformPages');
    useEffect(() => { document.title = t('paymentReturn.cancelTitle'); }, [t]);
    return (
        <div className="container py-5 text-center">
            <h3>{t('paymentReturn.cancelledHeading')}</h3>
            <p className="text-secondary">{t('paymentReturn.cancelledBody')}</p>
            <Link to="/billing" className="btn btn-primary mt-3">{t('paymentReturn.backToBilling')}</Link>
        </div>
    );
};
